const PHONE_PRIORITY = [
  'phoneNumber',
  'number',
  'phone',
  'PhoneNumber',
  'Phone',
  'Number'
];

const PHONE_ARRAY_FIELDS = [
  'phoneNumbers',
  'phoneNumbersArr',
  'phones'
];

export const normalizePhoneNumber = (phone) => {
  if (!phone) return phone;

  // Remove whitespace, dashes, parentheses and dots
  let cleaned = phone.replace(/[\s\-\(\)\.]/g, '');

  // Ensure leading +995 for Georgian numbers when possible
  if (cleaned.startsWith('+995')) {
    return cleaned;
  }

  if (cleaned.startsWith('995')) {
    return '+' + cleaned;
  }

  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  if (cleaned.startsWith('5') && cleaned.length === 9) {
    return '+995' + cleaned;
  }

  return cleaned;
};

export const extractUserPhoneNumbers = (userData) => {
  if (!userData) return [];

  const entries = [];

  const addEntry = (field, value, meta = {}) => {
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (!trimmed) return;

    entries.push({
      field,
      raw: value,
      normalized: normalizePhoneNumber(trimmed),
      ...meta
    });
  };

  PHONE_PRIORITY.forEach((field) => {
    if (userData[field]) {
      addEntry(field, userData[field]);
    }
  });

  PHONE_ARRAY_FIELDS.forEach((field) => {
    if (Array.isArray(userData[field])) {
      userData[field].forEach((value, index) => {
        addEntry(`${field}[${index}]`, value, { sourceField: field, index });
      });
    }
  });

  return entries;
};

const resolvePrimaryPhone = (entries) => {
  for (const field of PHONE_PRIORITY) {
    const match = entries.find((entry) => entry.field === field && entry.normalized);
    if (match) {
      return match.normalized;
    }
  }

  const firstEntry = entries.find((entry) => entry.normalized);
  return firstEntry ? firstEntry.normalized : null;
};

export const analyzeUserPhoneNumbers = (userData) => {
  const entries = extractUserPhoneNumbers(userData);
  const normalizedMap = new Map();

  entries.forEach((entry) => {
    if (!entry.normalized) return;
    if (!normalizedMap.has(entry.normalized)) {
      normalizedMap.set(entry.normalized, []);
    }
    normalizedMap.get(entry.normalized).push(entry);
  });

  const uniqueNormalized = Array.from(normalizedMap.keys());

  return {
    entries,
    normalizedMap,
    uniqueNormalized,
    canonical: resolvePrimaryPhone(entries),
    hasMultiple: uniqueNormalized.length > 1
  };
};

export const hasCompletedOnboarding = (userData) => {
  if (!userData) return false;
  const { uniqueNormalized, canonical } = analyzeUserPhoneNumbers(userData);
  if (!canonical) return false;
  if (uniqueNormalized.length !== 1) return false;

  const hasCardNumber = !!(userData.CardNumber || userData.cardNumber);
  return hasCardNumber;
};

export const getUserPhoneNumber = (userData) => {
  if (!userData) return null;
  const { canonical } = analyzeUserPhoneNumbers(userData);
  return canonical || null;
};

export const getUserEmail = (userData) => {
  return userData?.email || userData?.post || userData?.mail || null;
};

export const getUserCardNumber = (userData) => {
  return userData?.cardNumber || userData?.CardNumber || null;
};

export const getUserFullName = (userData) => {
  if (userData?.firstName && userData?.lastName) {
    return `${userData.firstName} ${userData.lastName}`;
  }
  return userData?.name || '';
};

export const saveUserSession = (userData, user) => {
  const fullName = getUserFullName(userData) || user.displayName || '';
  const userLogin = userData.login || user.email?.split('@')[0] || '';
  const userEmail = userData.email || user.email || '';
  const userPersonalId = userData.personalId || '';

  localStorage.setItem('LOGstatus', 'true');
  localStorage.setItem('USER_NAME', fullName);
  localStorage.setItem('USER_LOGIN', userLogin);
  localStorage.setItem('USER_POST', userEmail);
  localStorage.setItem('USER_CASH', userData.coin || 0);
  localStorage.setItem('USER_ID', user.uid);
  localStorage.setItem('USER_PERSONAL_ID', userPersonalId);

  return { fullName, userLogin, userEmail, userPersonalId, cash: userData.coin || 0 };
};
